import React, { lazy, Suspense, useContext } from 'react';
import { Navigate, useLocation, type RouteObject } from 'react-router-dom';
import Load from '../../atomicDesign/molecules/Load';
import EnergyLabelContext, {
  type EnergyLabelContextType,
} from '../../context/energyLabel';
import { FIRST_STEP, STEPS, SUBMITTED_PATH, type StepId } from './steps/config';

const stepComponents: Record<
  StepId,
  React.LazyExoticComponent<React.ComponentType>
> = {
  confirmBuilding: lazy(() => import('./steps/ConfirmBuilding')),
  aboutBuilding: lazy(() => import('./steps/AboutBuilding')),
  envelope: lazy(() => import('./steps/Envelope')),
  basement: lazy(() => import('./steps/Basement')),
  windows: lazy(() => import('./steps/Windows')),
  doors: lazy(() => import('./steps/Doors')),
  energySources: lazy(() => import('./steps/EnergySources')),
  centralHeating: lazy(() => import('./steps/CentralHeating')),
  heatPump: lazy(() => import('./steps/HeatPump')),
  boiler: lazy(() => import('./steps/Boiler')),
  solar: lazy(() => import('./steps/Solar')),
  ventilation: lazy(() => import('./steps/Ventilation')),
  heating: lazy(() => import('./steps/Heating')),
  summary: lazy(() => import('./steps/Summary')),
};

const Submitted = lazy(() => import('./Submitted'));

/**
 * Sends the bare flow URL to the first step, or to the sent-in page once the
 * form has been submitted, keeping ?lang and ?theme.
 */
function FirstStepRedirect() {
  const { search } = useLocation();
  const { submission } = useContext(
    EnergyLabelContext
  ) as EnergyLabelContextType;
  const sent = submission != null && submission.status !== 'DRAFT';
  return (
    <Navigate
      to={{ pathname: sent ? SUBMITTED_PATH : FIRST_STEP.path, search }}
      replace
    />
  );
}

/** Child routes of the energy label flow: one route per step. */
export const energyLabelFormRoutes: RouteObject[] = [
  { index: true, element: <FirstStepRedirect /> },
  {
    path: SUBMITTED_PATH,
    element: (
      <Suspense fallback={<Load />}>
        <Submitted />
      </Suspense>
    ),
  },
  ...STEPS.map((step) => {
    const Component = stepComponents[step.id];
    return {
      path: step.path,
      element: (
        <Suspense fallback={<Load />}>
          <Component />
        </Suspense>
      ),
    };
  }),
];
